import React, { useState, useEffect } from 'react';
import { 
  Card,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { dashboardAPI } from '../api/api';
import { getColorForChartType } from '../utils/colorUtils';

const CostOverviewChart = () => {
  const [timeRange, setTimeRange] = useState('monthly');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCostData = async () => {
      setLoading(true);
      try {
        const response = await dashboardAPI.getCostGraph(timeRange);
        // Format the period for better display based on the time range
        const formattedData = (response.data || []).map(item => ({
          ...item,
          // Format the period based on the time range for better display
          month: new Date(item.period).toLocaleDateString('en-US', 
            timeRange === 'daily' ? { month: 'short', day: 'numeric', year: 'numeric' } :
            timeRange === 'weekly' ? { month: 'short', day: 'numeric' } :
            timeRange === 'monthly' ? { month: 'short', year: 'numeric' } :
            timeRange === 'quarterly' ? { month: 'short', year: 'numeric' } :
            { year: 'numeric' } // yearly
          )
        }));
        // Sort the data by date to ensure proper ordering
        const sortedData = formattedData.sort((a, b) => new Date(a.period) - new Date(b.period));
        setChartData(sortedData);
      } catch (error) {
        console.error('Error fetching cost data:', error);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCostData();
  }, [timeRange]);

  return (
    <Card 
      elevation={0} 
      sx={{ 
        p: 3, 
        backgroundColor: 'background.paper',
        borderRadius: 3,
        height: '100%',
        boxShadow: 1
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Cost Overview
        </Typography>
        <FormControl sx={{ minWidth: 120 }} size="small">
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ height: 350 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 50, // Increased bottom margin for rotated labels
              }}
            >
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
              <XAxis 
                dataKey="month" 
                angle={-45} 
                textAnchor="end"
                height={50}
                tick={{ fill: '#666', fontSize: 11 }}
              />
              <YAxis 
                tick={{ fill: '#666' }}
                tickFormatter={(value) => `${value}`}
              />
              <Tooltip 
                formatter={(value) => [`${value.toFixed(2)}`, 'Cost']}
                labelFormatter={(label) => `Period: ${label}`}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
              {/* Remove legend for single-value charts */}
              <Bar 
                dataKey="total_amount" 
                name="Cost" 
                fill={getColorForChartType("Total Cost", 'bar')} 
                radius={[4, 4, 0, 0]} // Rounded corners for bars
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Card>
  );
};

export default CostOverviewChart;